import * as i0 from '@angular/core';
import { inject, Component, ChangeDetectionStrategy, ViewChild, PLATFORM_ID, Injectable, makeEnvironmentProviders, APP_INITIALIZER } from '@angular/core';
import { Subject, BehaviorSubject, fromEvent } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
const _c0 = ["ngxCookiebot"];
class NgxCookiebotConfig {
  blockingMode;
  consentMode;
  cbId;
  cdn;
  culture;
  framework;
  level;
  loadScript;
  type;
  widgetEnabled;
  widgetPosition;
  widgetDistanceVertical;
  widgetDistanceHorizontal;
}
let NgxCookiebotComponent = /*#__PURE__*/(() => {
  class NgxCookiebotComponent {
    #ngxCookiebotConfig = inject(NgxCookiebotConfig);
    ngxCookiebotElement;
    ngOnInit() {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.id = 'CookieDeclaration';
      script.src = 'https://consent.cookiebot.' + this.#ngxCookiebotConfig.cdn + '/' + this.#ngxCookiebotConfig.cbId + '/cd.js';
      script.async = true;
      this.ngxCookiebotElement.nativeElement.append(script);
    }
    /** @nocollapse */
    static ɵfac = function NgxCookiebotComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NgxCookiebotComponent)();
    };
    /** @nocollapse */
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NgxCookiebotComponent,
      selectors: [["ngx-cookiebot-declaration"]],
      viewQuery: function NgxCookiebotComponent_Query(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵviewQuery(_c0, 7);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.ngxCookiebotElement = _t.first);
        }
      },
      standalone: true,
      features: [i0.ɵɵStandaloneFeature],
      decls: 2,
      vars: 0,
      consts: [["ngxCookiebot", ""]],
      template: function NgxCookiebotComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵelement(0, "div", null, 0);
        }
      },
      styles: ["[_nghost-%COMP%]{display:block}"],
      changeDetection: 0
    });
  }
  return NgxCookiebotComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function getWindow() {
  return window;
}
/**
 * Exposes the 'Cookiebot' object provided by the Cookiebot SDK
 * https://www.cookiebot.com/en/developer/
 */
let NgxCookiebotService = /*#__PURE__*/(() => {
  class NgxCookiebotService {
    #cookiebotConfig = inject(NgxCookiebotConfig);
    #platformId = inject(PLATFORM_ID);
    #onAcceptCallback$ = new Subject();
    #onDeclineCallback$ = new Subject();
    #onDialogDisplayCallback$ = new Subject();
    #onDialogInitCallback$ = new Subject();
    #onLoadCallback$ = new Subject();
    #onServiceReady$ = new BehaviorSubject(false);
    #onTagsExecutedCallback$ = new Subject();
    #window = new Subject();
    cookiebot;
    onAccept$;
    onLoad$;
    onConsentReady$;
    onDecline$;
    onDialogInit$;
    onDialogDisplay$;
    onTagsExecuted$;
    onAcceptCallback$ = this.#onAcceptCallback$.asObservable();
    onDeclineCallback$ = this.#onDeclineCallback$.asObservable();
    onDialogDisplayCallback$ = this.#onDialogDisplayCallback$.asObservable();
    onDialogInitCallback$ = this.#onDialogInitCallback$.asObservable();
    onLoadCallback$ = this.#onLoadCallback$.asObservable();
    onServiceReady$ = this.#onServiceReady$.asObservable();
    onTagsExecutedCallback$ = this.#onTagsExecutedCallback$.asObservable();
    constructor() {
      if (isPlatformBrowser(this.#platformId)) {
        this.#verifyConfig();
        this.#window = getWindow();
      }
    }
    init() {
      if (!isPlatformBrowser(this.#platformId)) {
        return new Promise(resolve => {
          resolve();
        });
      }
      return new Promise(resolve => {
        try {
          if (this.#cookiebotConfig.loadScript !== false) {
            this.#window.document.head.append(this.#buildScriptTag());
          }
        } catch (e) {
          this.#onServiceReady$.error(e);
          return resolve();
        }
        const scriptInjectionTimeout = setTimeout(() => {
          this.#onServiceReady$.error('Timed out');
          clearInterval(scriptInjectionCheckInterval);
        }, 30000); // 30 seconds
        const scriptInjectionCheckInterval = setInterval(() => {
          // The Cookiebot people added and ID to the script tag
          // with the same name as the object it exposes
          // https://twitter.com/jacksdrobinson/status/1188152645032255491
          if (!(this.#window.Cookiebot instanceof HTMLElement)) {
            this.cookiebot = this.#window.Cookiebot;
            this.#setUpCallbacks();
            this.#setUpEventHandlers();
            clearInterval(scriptInjectionCheckInterval);
            clearTimeout(scriptInjectionTimeout);
            this.#onServiceReady$.next(true);
          }
        }, 10);
        return resolve();
      });
    }
    #buildScriptTag() {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.id = 'Cookiebot';
      script.src = 'https://consent.cookiebot.' + this.#cookiebotConfig.cdn + '/uc.js';
      script.setAttribute('data-cbid', this.#cookiebotConfig.cbId);
      if ('auto' === this.#cookiebotConfig.blockingMode) {
        script.setAttribute('data-blockingmode', 'auto');
      } else {
        script.async = true;
      }
      if ('disabled' === this.#cookiebotConfig.consentMode) {
        script.setAttribute('data-consentmode', this.#cookiebotConfig.consentMode);
      }
      if (this.#cookiebotConfig.culture) {
        script.setAttribute('data-culture', this.#cookiebotConfig.culture);
      }
      if (this.#cookiebotConfig.framework) {
        script.setAttribute('data-framework', this.#cookiebotConfig.framework);
      }
      if (this.#cookiebotConfig.level) {
        script.setAttribute('data-level', this.#cookiebotConfig.level);
      }
      if (this.#cookiebotConfig.type) {
        script.setAttribute('data-type', this.#cookiebotConfig.type);
      }
      if (this.#cookiebotConfig.widgetEnabled) {
        script.setAttribute('data-widget-enabled', this.#cookiebotConfig.widgetEnabled ? 'true' : 'false');
      }
      if (this.#cookiebotConfig.widgetPosition) {
        script.setAttribute('data-widget-position', this.#cookiebotConfig.widgetPosition);
      }
      if (this.#cookiebotConfig.widgetDistanceVertical) {
        script.setAttribute('data-widget-distance-vertical', this.#cookiebotConfig.widgetDistanceVertical.toString());
      }
      if (this.#cookiebotConfig.widgetDistanceHorizontal) {
        script.setAttribute('data-widget-distance-horizontal', this.#cookiebotConfig.widgetDistanceHorizontal.toString());
      }
      return script;
    }
    #setUpCallbacks() {
      /* eslint-disable @typescript-eslint/ban-ts-comment */
      // @ts-ignore
      window.CookiebotCallback_OnAccept = () => {
        this.#onAcceptCallback$.next();
      };
      // @ts-ignore
      window.CookiebotCallback_OnDecline = () => {
        this.#onDeclineCallback$.next();
      };
      // @ts-ignore
      window.CookiebotCallback_OnDialogDisplay = () => {
        this.#onDialogDisplayCallback$.next();
      };
      // @ts-ignore
      window.CookiebotCallback_OnDialogInit = () => {
        this.#onDialogInitCallback$.next();
      };
      // @ts-ignore
      window.CookiebotCallback_OnLoad = () => {
        this.#onLoadCallback$.next();
      };
      // @ts-ignore
      window.CookiebotCallback_OnTagsExecuted = () => {
        this.#onTagsExecutedCallback$.next();
      };
      /* eslint-enable @typescript-eslint/ban-ts-comment */
    }
    #setUpEventHandlers() {
      this.onAccept$ = fromEvent(this.#window, 'CookiebotOnAccept');
      this.onConsentReady$ = fromEvent(this.#window, 'CookiebotOnConsentReady');
      this.onDecline$ = fromEvent(this.#window, 'CookiebotOnDecline');
      this.onDialogInit$ = fromEvent(this.#window, 'CookiebotOnDialogInit');
      this.onDialogDisplay$ = fromEvent(this.#window, 'CookiebotOnDialogDisplay');
      this.onLoad$ = fromEvent(this.#window, 'CookiebotOnLoad');
      this.onTagsExecuted$ = fromEvent(this.#window, 'CookiebotOnTagsExecuted');
    }
    #verifyConfig() {
      if (typeof this.#cookiebotConfig.loadScript !== 'boolean') {
        throw new Error('Wrong loadScript config value. Please provide a correct value in the Cookiebot config');
      }
      // Nothing to validate if script is manually set
      if (this.#cookiebotConfig.loadScript === false) {
        return;
      }
      if (!this.#cookiebotConfig.cdn || !['com', 'eu'].includes(this.#cookiebotConfig.cdn)) {
        throw new Error('Wrong cdn config value. Please provide a correct value in the Cookiebot config');
      }
      if (!this.#cookiebotConfig.cbId) {
        throw new Error('Wrong cbId config value. Please provide a correct value in the Cookiebot config');
      }
      if (!this.#cookiebotConfig.blockingMode || !['auto', 'manual'].includes(this.#cookiebotConfig.blockingMode)) {
        throw new Error('Wrong blockingMode config value. Please provide a correct value in the Cookiebot config');
      }
    }
    /** @nocollapse */
    static ɵfac = function NgxCookiebotService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NgxCookiebotService)();
    };
    /** @nocollapse */
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: NgxCookiebotService,
      factory: NgxCookiebotService.ɵfac,
      providedIn: 'root'
    });
  }
  return NgxCookiebotService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function ngxCookiebotFactory(cookiebotService) {
  return () => {
    return cookiebotService.init();
  };
}
function ngxCookiebotProvider(cookiebotConfig) {
  return makeEnvironmentProviders([{
    provide: NgxCookiebotConfig,
    useClass: cookiebotConfig
  }, {
    provide: NgxCookiebotService,
    useClass: NgxCookiebotService,
    deps: [NgxCookiebotConfig, PLATFORM_ID]
  }, {
    provide: APP_INITIALIZER,
    useFactory: ngxCookiebotFactory,
    deps: [NgxCookiebotService, PLATFORM_ID],
    multi: true
  }]);
}

/*
 * Public API Surface of ngx-cookiebot
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NgxCookiebotComponent, NgxCookiebotConfig, NgxCookiebotService, ngxCookiebotProvider };
